import { useCallback, useMemo, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { App, Skeleton } from 'antd'
import { ArrowLeft } from 'lucide-react'
import { useMindmapData } from '../ReportPage/hooks/useMindmapData'
import { StartResearchRoundButton } from '../../components/Mindmap/Panels/MindMapHeader/components'
import type { ReenterSelectionState } from '../../features/report'
import { MindmapSurveyService } from '../../features/survey/services/mindmapSurveyService'
import { MindmapInterviewService } from '../../features/interview/services/mindmapInterviewService'
import { DocumentService } from '../../services/documents/documentService'
import { SurveyCard } from './SurveyCard'
import { InterviewCard } from './InterviewCard'
import {
  UploadedResearchCard,
  ventureDocumentToUploadedItem,
  type UploadedResearchItem,
} from './UploadedResearchCard'
import { DocumentSummaryModal } from './DocumentSummaryModal'
import { ResearchToolbar, type ResearchTab } from './ResearchToolbar'
import type { ResearchItem } from './types'
import { useResearchData } from './useResearchData'
import { surveyToItem, interviewToItem } from './researchPageUtils'
import {
  PageContainer,
  PageHeader,
  HeaderActions,
  BackButton,
  HeaderInfo,
  PageTitle,
  PageSubtitle,
  SectionTitle,
  SectionHeader,
  SectionHeaderTitle,
  SectionHeaderCount,
  CardsGrid,
  RowContainer,
  EmptySection,
} from './ResearchPageStyles'

export function ResearchPage() {
  const { id: mindmapId } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { loading: mindmapLoading, mindmap } = useMindmapData(mindmapId)
  const { modal, notification } = App.useApp()

  const {
    surveys,
    setSurveys,
    interviews,
    setInterviews,
    uploadedDocuments,
    setUploadedDocuments,
    loading: recordsLoading,
  } = useResearchData(mindmapId)

  const [tab, setTab] = useState<ResearchTab>('all')
  const [searchQuery, setSearchQuery] = useState('')

  const allSurveyItems = useMemo(() => surveys.map(surveyToItem), [surveys])
  const allInterviewItems = useMemo(() => interviews.map(interviewToItem), [interviews])

  const matchesSearch = useCallback(
    (haystack: (string | null | undefined)[]) => {
      const needle = searchQuery.trim().toLowerCase()
      if (!needle) return true
      return haystack.some((h) => h?.toLowerCase().includes(needle))
    },
    [searchQuery]
  )

  const surveyItems = useMemo(
    () =>
      allSurveyItems.filter((item) =>
        matchesSearch([item.label, item.description, item.parentLabel])
      ),
    [allSurveyItems, matchesSearch]
  )
  const interviewItems = useMemo(
    () =>
      allInterviewItems.filter((item) =>
        matchesSearch([item.label, item.description, item.parentLabel])
      ),
    [allInterviewItems, matchesSearch]
  )
  const filteredUploadedDocuments = useMemo(
    () =>
      uploadedDocuments.filter((doc) => matchesSearch([doc.file_name, doc.content_classification])),
    [uploadedDocuments, matchesSearch]
  )

  const totalCount = allSurveyItems.length + allInterviewItems.length + uploadedDocuments.length
  const filteredTotalCount =
    surveyItems.length + interviewItems.length + filteredUploadedDocuments.length
  const loading = mindmapLoading || recordsLoading

  const showSurveys = tab === 'all' || tab === 'survey'
  const showInterviews = tab === 'all' || tab === 'interview'
  const showUploaded = tab === 'all' || tab === 'uploaded'

  const hasValidatableContent = useMemo(
    () => (mindmap?.content.nodes ?? []).some((n) => n.data.category !== 'venture'),
    [mindmap?.content.nodes]
  )

  const handleStartResearch = useCallback(() => {
    if (!mindmapId) return
    const reenterSelection: ReenterSelectionState = {
      nodeIds: [],
      storageKey: `validate-selection-${mindmapId}`,
      destination: `/mindmap/${mindmapId}/validate`,
      title: 'What do you want to validate?',
      subtitle: 'Pick the needs and ideas you want to test',
    }
    void navigate(`/mindmap/${mindmapId}`, { state: { reenterSelection } })
  }, [navigate, mindmapId])

  const performDelete = useCallback(
    async (item: ResearchItem) => {
      if (!mindmapId) return
      const previousSurveys = surveys
      const previousInterviews = interviews
      if (item.type === 'survey') {
        setSurveys((prev) => prev.filter((s) => s.id !== item.id))
      } else {
        setInterviews((prev) => prev.filter((i) => i.id !== item.id))
      }
      const res =
        item.type === 'survey'
          ? await MindmapSurveyService.delete(mindmapId, item.id)
          : await MindmapInterviewService.delete(mindmapId, item.id)
      if (res.error) {
        setSurveys(previousSurveys)
        setInterviews(previousInterviews)
        notification.error({ title: 'Failed to delete', description: res.error.message })
        return
      }
      notification.success({
        title: item.type === 'survey' ? 'Survey deleted' : 'Interview deleted',
      })
    },
    [mindmapId, surveys, interviews, setSurveys, setInterviews, notification]
  )

  const handleDelete = useCallback(
    (item: ResearchItem) => {
      const noun = item.type === 'survey' ? 'survey' : 'interview'
      const dataNoun = item.type === 'survey' ? 'responses' : 'notes'
      modal.confirm({
        title: `Delete this ${noun}?`,
        content: `"${item.label || `Untitled ${noun}`}" and all of its ${dataNoun} will be permanently deleted. This can't be undone.`,
        okText: 'Delete',
        okButtonProps: { danger: true },
        cancelText: 'Cancel',
        onOk: () => performDelete(item),
      })
    },
    [modal, performDelete]
  )

  const performDeleteDocument = useCallback(
    async (item: UploadedResearchItem) => {
      setUploadedDocuments((prev) => prev.filter((d) => d.id !== item.id))
      try {
        await DocumentService.deleteDocument(item.id)
        notification.success({ title: 'File deleted' })
      } catch (err) {
        notification.error({
          title: 'Failed to delete file',
          description: err instanceof Error ? err.message : undefined,
        })
        // Re-fetch to restore accurate state rather than risk stale rollback
        if (mindmapId) {
          void DocumentService.listDocumentsForMindmap(mindmapId)
            .then((docs) => setUploadedDocuments(docs.map(ventureDocumentToUploadedItem)))
            .catch(() => undefined)
        }
      }
    },
    [mindmapId, setUploadedDocuments, notification]
  )

  const handleDeleteDocument = useCallback(
    (item: UploadedResearchItem) => {
      modal.confirm({
        title: 'Delete this file?',
        content: `"${item.file_name}" will be permanently deleted. This can't be undone.`,
        okText: 'Delete',
        okButtonProps: { danger: true },
        cancelText: 'Cancel',
        onOk: () => performDeleteDocument(item),
      })
    },
    [modal, performDeleteDocument]
  )

  const [selectedDocument, setSelectedDocument] = useState<UploadedResearchItem | null>(null)

  const handleDocumentClick = useCallback((item: UploadedResearchItem) => {
    setSelectedDocument(item)
  }, [])

  const handleDocumentModalClose = useCallback(() => {
    setSelectedDocument(null)
  }, [])

  return (
    <PageContainer>
      <PageHeader>
        <BackButton onClick={() => void navigate(`/mindmap/${mindmapId}`)} title="Back to mindmap">
          <ArrowLeft size={18} />
        </BackButton>
        <HeaderInfo>
          <PageTitle>Research</PageTitle>
          <PageSubtitle>
            {loading
              ? 'Loading…'
              : mindmap
                ? `${mindmap.title} · ${totalCount} item${totalCount !== 1 ? 's' : ''}`
                : ''}
          </PageSubtitle>
        </HeaderInfo>
        <HeaderActions>
          <StartResearchRoundButton
            enabled={!mindmapLoading && hasValidatableContent}
            onClick={handleStartResearch}
          />
        </HeaderActions>
      </PageHeader>

      {loading ? (
        <Skeleton active paragraph={{ rows: 6 }} />
      ) : (
        <>
          <ResearchToolbar
            tab={tab}
            onTabChange={setTab}
            counts={{
              all: totalCount,
              survey: allSurveyItems.length,
              interview: allInterviewItems.length,
              uploaded: uploadedDocuments.length,
            }}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
          />

          {filteredTotalCount === 0 && searchQuery.trim() ? (
            <EmptySection>No results for &quot;{searchQuery}&quot;.</EmptySection>
          ) : (
            <>
              {showSurveys && (
                <>
                  <SectionHeader>
                    <SectionHeaderTitle>Surveys</SectionHeaderTitle>
                    <SectionHeaderCount>{surveyItems.length}</SectionHeaderCount>
                  </SectionHeader>
                  {surveyItems.length > 0 ? (
                    <RowContainer>
                      {surveyItems.map((item) => (
                        <SurveyCard key={item.id} item={item} onDelete={handleDelete} />
                      ))}
                    </RowContainer>
                  ) : (
                    <EmptySection>
                      {searchQuery.trim()
                        ? 'No surveys match your search.'
                        : 'No surveys yet — validate ideas from the mindmap to create one.'}
                    </EmptySection>
                  )}
                </>
              )}

              {showInterviews && (
                <>
                  <SectionHeader>
                    <SectionHeaderTitle>Interviews</SectionHeaderTitle>
                    <SectionHeaderCount>{interviewItems.length}</SectionHeaderCount>
                  </SectionHeader>
                  {interviewItems.length > 0 ? (
                    <RowContainer>
                      {interviewItems.map((item) => (
                        <InterviewCard key={item.id} item={item} onDelete={handleDelete} />
                      ))}
                    </RowContainer>
                  ) : (
                    <EmptySection>
                      {searchQuery.trim()
                        ? 'No interviews match your search.'
                        : 'No interviews yet — validate ideas from the mindmap to create one.'}
                    </EmptySection>
                  )}
                </>
              )}

              {showUploaded && (
                <>
                  <SectionTitle>Uploaded Research</SectionTitle>
                  {filteredUploadedDocuments.length > 0 ? (
                    <CardsGrid>
                      {filteredUploadedDocuments.map((item) => (
                        <UploadedResearchCard
                          key={item.id}
                          item={item}
                          onDelete={handleDeleteDocument}
                          onClick={handleDocumentClick}
                        />
                      ))}
                    </CardsGrid>
                  ) : (
                    <EmptySection>
                      {searchQuery.trim()
                        ? 'No files match your search.'
                        : 'No files uploaded yet — add supporting documents from the venture node.'}
                    </EmptySection>
                  )}
                </>
              )}
            </>
          )}
        </>
      )}

      <DocumentSummaryModal item={selectedDocument} onClose={handleDocumentModalClose} />
    </PageContainer>
  )
}
